import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const EMPTY_INPUTS = {
  currency: null, package_key: 'auto', term_years: 3, start_date: '', escalation_pct: null, term_discount_override_pct: null, term_discount_override_reason: '', term_discount_note: '',
  quantities: { active_users: '', enrollment_blocks: 1, private_cloud: false, storage_tb: '', oa_app: 0, video_streaming_gb: '', video_storage_gb: '',
    analytics_author: '', analytics_user: '', api_tokens: '', pdp_app: 0, test_environments: 1, mission_control: 0, hosted_vanity_domain: 0 },
  discounts: {}, custom_prices: {}, custom_costs: {}, notes: '',
  support: { enabled: false, mode: 'block', block_key: '', quantity: 1, hours: '', repeat_annually: false, discount_mode: 'pct', discount_value: '', discount_reason: '', expiry_date: '' },
  support_lines: [],
};

export const supportDiscountMissingReason = (inp) => !!inp?.support?.enabled && Number(inp.support.discount_value) > 0 && !(inp.support.discount_reason || '').trim();

export const suggestedTermNote = (preview) => {
  if (!preview) return '';
  const pct = preview.term_discount_pct;
  if (!pct) return 'No multi-year term discount has been applied; subscription pricing is on annual terms.';
  return `Pricing includes a ${pct}% multi-year commitment discount on Active Users for the ${preview.term_years}-year term.`;
};

export const termOverrideMissingReason = (inp) => inp?.term_discount_override_pct != null && inp.term_discount_override_pct !== '' && !(inp.term_discount_override_reason || '').trim();

export const cleanInputs = (inp) => ({
  ...inp,
  start_date: inp.start_date || null,
  escalation_pct: inp.escalation_pct === '' || inp.escalation_pct == null ? null : Number(inp.escalation_pct),
  term_discount_override_pct: inp.term_discount_override_pct === '' || inp.term_discount_override_pct == null ? null : Number(inp.term_discount_override_pct),
  term_discount_override_reason: inp.term_discount_override_pct === '' || inp.term_discount_override_pct == null ? null : (inp.term_discount_override_reason || '').trim(),
  term_discount_note: (inp.term_discount_note || '').trim() || null,
  quantities: Object.fromEntries(Object.entries(inp.quantities || {}).map(([k, v]) => [k, typeof v === 'boolean' ? v : (v === '' || v == null ? 0 : Number(v))])),
  support: inp.support ? { ...inp.support, quantity: Number(inp.support.quantity || 1), hours: inp.support.hours === '' || inp.support.hours == null ? 0 : Number(inp.support.hours),
    discount_mode: inp.support.discount_mode || 'pct', discount_value: inp.support.discount_value === '' || inp.support.discount_value == null ? 0 : Number(inp.support.discount_value),
    discount_reason: Number(inp.support.discount_value) > 0 ? (inp.support.discount_reason || '').trim() : '', expiry_date: inp.support.expiry_date || null } : null,
  support_lines: (inp.support_lines || []).map((l) => ({ ...l, discount_value: Number(l.discount_value || 0), expiry_date: l.expiry_date || null })),
  custom_prices: Object.fromEntries(Object.entries(inp.custom_prices || {}).filter(([, v]) => v !== '' && v != null).map(([k, v]) => [k, Number(v)])),
  custom_costs: Object.fromEntries(Object.entries(inp.custom_costs || {}).filter(([, v]) => v !== '' && v != null).map(([k, v]) => [k, Number(v)])),
  discounts: Object.fromEntries(Object.entries(inp.discounts || {}).filter(([, d]) => Number(d?.pct) > 0).map(([k, d]) => [k, { pct: Number(d.pct), reason: d.reason || '' }])),
});

/** Loads the active price book and keeps a debounced live preview in sync with `inputs`. */
export function useProductPricing(initialInputs, estimateId) {
  const [book, setBook] = useState(null);
  const [currencies, setCurrencies] = useState([]);
  const [components, setComponents] = useState([]);
  const [inputs, setInputs] = useState(initialInputs || EMPTY_INPUTS);
  const [preview, setPreview] = useState(null);
  const [previewError, setPreviewError] = useState(null);
  const [loading, setLoading] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    axios.get(`${API_URL}/api/product-pricing/price-book`, { params: inputs.currency ? { currency: inputs.currency } : {}, withCredentials: true })
      .then((r) => { setBook(r.data.book); setComponents(r.data.components); setCurrencies(r.data.currencies || []); })
      .catch(() => setPreviewError('Could not load the price book'));
  }, [inputs.currency]);

  useEffect(() => {
    if (!book) return;
    if (timer.current) clearTimeout(timer.current);
    const q = cleanInputs(inputs).quantities;
    if (!Object.values(q).some((v) => v === true || (typeof v === 'number' && v > 0))) { setPreview(null); setPreviewError(null); return undefined; }
    timer.current = setTimeout(() => {
      setLoading(true);
      axios.post(`${API_URL}/api/product-pricing/preview`, { ...cleanInputs(inputs), estimate_id: estimateId || null }, { withCredentials: true })
        .then((r) => { setPreview(r.data); setPreviewError(null); })
        .catch((e) => setPreviewError(e?.response?.data?.detail || 'Preview failed'))
        .finally(() => setLoading(false));
    }, 350);
    return () => timer.current && clearTimeout(timer.current);
  }, [inputs, book, estimateId]);

  const setField = useCallback((k, v) => setInputs((p) => ({ ...p, [k]: v })), []);
  const setQty = useCallback((k, v) => setInputs((p) => ({ ...p, quantities: { ...p.quantities, [k]: v } })), []);
  const setDiscount = useCallback((k, patch) => setInputs((p) => ({ ...p, discounts: { ...p.discounts, [k]: { ...(p.discounts[k] || {}), ...patch } } })), []);
  const setCustom = useCallback((field, k, v) => setInputs((p) => ({ ...p, [field]: { ...p[field], [k]: v } })), []);

  return { book, currencies, components, inputs, setInputs, setField, setQty, setDiscount, setCustom, preview, previewError, loading };
}
